import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiCheckLine, RiFileCopyLine } from '@remixicon/react'
import type { AdminResetLink } from 'shared'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '../ui'

interface ResetLinkDialogProps {
  /** L'account per cui si chiede il link. `null` chiude il dialogo. */
  user: { id: string; name: string; role: string } | null
  onClose: () => void
  /** Il link è stato emesso: serve a ricaricare il registro, che ha una riga nuova. */
  onIssued: () => void
}

/**
 * Il link di reimpostazione che l'admin consegna a mano.
 *
 * Due passi, di proposito. Prima l'avviso: con questo link si sceglie la
 * password di un account attivo, quindi si può entrare come quella persona, e
 * l'azione resta nel registro. Solo dopo la conferma il link viene creato e si
 * mostra, una volta sola: nel database c'è l'hash, e non si rivede.
 */
export default function ResetLinkDialog({ user, onClose, onIssued }: ResetLinkDialogProps) {
  const { t } = useTranslation()
  const [issued, setIssued] = useState<AdminResetLink | null>(null)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  // Mentre il dialogo si chiude `user` è già `null`: il nome resta quello di chi
  // era, o per un attimo la frase direbbe «consegna questo link a ».
  const [target, setTarget] = useState(user)
  // L'account della richiesta in corso. Una risposta che arriva dopo che il
  // dialogo è stato chiuso o riaperto per un altro non deve scrivere un link
  // sotto il nome sbagliato: sarebbe la credenziale di una persona consegnata a
  // un'altra.
  const current = useRef<string | null>(null)

  // Ogni apertura riparte dall'avviso: un link di prima non deve restare a schermo.
  useEffect(() => {
    current.current = user?.id ?? null
    if (user) setTarget(user)
    setIssued(null)
    setError(null)
    setCopied(false)
    setWorking(false)
  }, [user])

  const issue = async () => {
    // Un secondo clic prima che il primo sia tornato emetterebbe un secondo link
    // valido e consumerebbe un altro dei tre tentativi all'ora.
    if (!user || working) return
    const asked = user.id
    setWorking(true)
    setError(null)
    try {
      const response = await api.post<AdminResetLink>(
        `/api/admin/users/${asked}/password-reset-link`,
        { confirm: true }
      )
      // Il link esiste e il registro lo sa comunque: si ricarica sempre. Ma si
      // mostra solo se il dialogo è ancora per la stessa persona.
      onIssued()
      if (current.current === asked) setIssued(response)
    } catch (err) {
      if (current.current === asked) setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      if (current.current === asked) setWorking(false)
    }
  }

  const copy = async () => {
    if (!issued) return
    // Il campo resta selezionabile: se la copia è negata si copia a mano, e il
    // link non si rivede.
    try {
      await navigator.clipboard.writeText(issued.reset_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // Con il link a schermo non si chiude per sbaglio: un clic fuori o Esc lo
        // perderebbero, e un altro costa un tentativo e lascia una riga in più.
        // Si chiude con il pulsante.
        onEscapeKeyDown={(event) => issued && event.preventDefault()}
        onInteractOutside={(event) => issued && event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.resetLinkTitle')}</DialogTitle>
          <DialogDescription>
            {issued
              ? t('admin.resetLinkIssuedDescription', {
                  name: user?.name ?? '',
                  minutes: issued.expires_in_minutes,
                })
              : t('admin.resetLinkDescription', { name: target?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <>
            <div className="flex gap-2">
              <Input
                readOnly
                value={issued.reset_url}
                aria-label={t('admin.resetLinkTitle')}
                className="font-mono text-xs"
              />
              <Button type="button" variant="secondary" onClick={copy}>
                {copied ? (
                  <RiCheckLine className="size-4 text-emerald-600" aria-hidden="true" />
                ) : (
                  <RiFileCopyLine className="size-4" aria-hidden="true" />
                )}
                <span className="sr-only">{t('common.copy')}</span>
              </Button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.resetLinkNotShownAgain')}
            </p>
          </>
        ) : (
          <>
            <Alert>{t('admin.resetLinkWarning')}</Alert>
            {/* Entrare come un altro admin è la cosa più pesante che questo link
                permetta: l'avviso lo dice a parte, con parole sue. */}
            {target?.role === 'admin' && <Alert>{t('admin.resetLinkWarningAdmin')}</Alert>}
          </>
        )}

        {error && <Alert>{error}</Alert>}

        <DialogFooter>
          {issued ? (
            <Button type="button" onClick={onClose}>
              {t('common.close')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="button" loading={working} onClick={issue}>
                {t('admin.resetLinkGenerate')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
